import sortBy from 'lodash/sortBy'
import api from '@/api'
import { _surfaceErrorMessage, _surfaceSuccessMessage } from '@/snackbar'
import { proficiencyValueToTooltipStringMap as proficiencyLevelClarificationMap } from '@/components/Rating.vue'
import { ENTITIES } from '../constants'
import { INTEGER_REPRESENTING_NO_EXPERIENCE } from '../helpers'

export const getters = {
  getFilteredUsersByNameSearch() {
    return (searchText) => {
      if (!searchText) return []
      let filteredUsers = []
      for (const id in this.activeUsers) {
        const user = this.activeUsers[id]
        if (user.name.toLowerCase().includes(searchText.toLowerCase())) {
          filteredUsers = [...filteredUsers, user]
        }
      }
      return sortBy(filteredUsers, ['name'])
    }
  },

  getFilteredConsultantsByNameSearch() {
    return (searchText) => {
      if (!searchText) return []
      let filteredConsultants = []
      for (const id in this.consultants) {
        const consultant = this.consultants[id]
        if (consultant.name.toLowerCase().includes(searchText.toLowerCase())) {
          filteredConsultants = [...filteredConsultants, consultant]
        }
      }
      return sortBy(filteredConsultants, ['name'])
    }
  },

  getUsersByIds: (state) => (userIds) => userIds.map((id) => state.users[id]),

  getSalesLeadUserNamesByIds() {
    return (salesLeadUserIds) =>
      this.getUsersByIds(salesLeadUserIds).map((salesLead) => salesLead?.name)
  },

  getDecoratedActiveUsersForTeamView() {
    return (skillId) => {
      let decoratedUsers = []
      for (const id in this.activeUsers) {
        const user = { ...this.activeUsers[id] }
        const proficiency = this.getRatingProficiencyForSkillIdAndUserId(skillId, user.id)
        user.managerDisplayString = this.getManagerNameByConsultant(user)
        user.htmlImgString = `<img class="user-image" alt="${user.name}" title="${user.name}" src="${user.imageUrl}" />`
        user.proficiencyInSelectedSkill = proficiency
        user.proficiencyInSelectedSkillDisplayString = `${proficiency} - ${proficiencyLevelClarificationMap[proficiency]}`
        user.tierDisplayString = `${user.tier == null ? '' : user.tier}`
        user.utilizationDisplayString = `${user.percentageUtilization}%`
        user.currentClientDisplayString = this.getCurrentClientNameByClientId(user.currentClientId)
        decoratedUsers = [...decoratedUsers, user]
      }
      if (!skillId) return decoratedUsers
      return decoratedUsers.filter(
        (user) => user.proficiencyInSelectedSkill > INTEGER_REPRESENTING_NO_EXPERIENCE
      )
    }
  },

  getDecoratedArchivedUsersForTeamView() {
    let decoratedUsers = []
    for (const id in this.archivedUsers) {
      const user = { ...this.archivedUsers[id] }
      user.htmlImgString = `<img class="user-image" alt="${user.name}" title="${user.name}" src="${user.imageUrl}" />`
      user.tierDisplayString = `${user.tier == null ? '' : user.tier}`
      user.utilizationDisplayString = `${user.percentageUtilization}%`
      user.currentClientDisplayString = this.getCurrentClientNameByClientId(user.currentClientId)
      decoratedUsers = [...decoratedUsers, user]
    }
    return decoratedUsers
  },

  getAllManagers() {
    const uniqueManagerIds = this.currentPlacedStaffingRoles.reduce((accumulator, role) => {
      if (role.managerId && !accumulator.includes(role.managerId)) {
        accumulator.push(role.managerId)
      }
      return accumulator
    }, [])
    return this.getUsersByIds(uniqueManagerIds)
  },

  getManagerNameByConsultant() {
    return (consultant) => {
      const currentPlacedStaffingRole = this.getCurrentPlacedStaffingRoleByConsultantId(
        consultant.id
      )
      if (!currentPlacedStaffingRole) return 'Bench Manager'
      if (!currentPlacedStaffingRole.managerId) return null
      const manager = this.getEntityById(ENTITIES.USERS, currentPlacedStaffingRole.managerId)
      return manager ? manager.name : null
    }
  },

  getTitles: (state) => () => state.titles || [],
}

export const actions = {
  async updateUser(payload) {
    try {
      const user = await api.updateUser(payload)
      this.users[user.id] = { ...this.users[user.id], ...user }
      _surfaceSuccessMessage('User updated!')
    } catch (err) {
      const error = err.data?.error
      _surfaceErrorMessage(`User not updated: ${error}`)
    }
  },

  async createUser(payload) {
    try {
      const user = await api.createUser(payload)
      this.users = { ...this.users, [user.id]: user }
      _surfaceSuccessMessage('User created!')
    } catch (errResponse) {
      const error = errResponse.data?.error
      _surfaceErrorMessage(`User not created: ${error}`)
    }
  },

  async fetchTitles() {
    this.titles = await api.fetchTitles()
  },
}
